import { Link } from 'react-router';
import { buildMeta, breadcrumbSchema } from '~/lib/seo';
import { SITE, GUARANTEE } from '~/data/site';

export function meta() {
  return buildMeta({
    title: 'Why us',
    description:
      'W-2 crews, a named supervisor, a signed checklist every visit, and a guarantee with a real definition. Accountability, written down rather than claimed.',
    path: '/why-us',
    jsonLd: breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Why us', url: '/why-us' },
    ]),
  });
}

/**
 * The accountability page.
 *
 * Each block is gated where it makes a factual claim. A pledge we intend to keep is
 * written as a commitment; a credential we do not yet hold does not appear at all.
 */
export default function WhyUs() {
  return (
    <>
      <section className="hero dusk grain">
        <div className="shell">
          <div className="hero-inner">
            <p className="eyebrow-pill">Why us</p>
            <h1>Cleaning is easy. Showing up is the product.</h1>
            <p className="lede">
              Industry churn runs 25–35% a year, and almost none of it is about dirt. It is about
              vendors who cannot prove the work got done.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="section-head">
            <p className="eyebrow">How we are built</p>
            <h2>Six decisions, made on purpose.</h2>
          </div>

          <div className="grid-2">
            {PILLARS.map((p) => (
              <div key={p.title} className="card">
                <h4>{p.title}</h4>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="night-band">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">The checklist is the contract</p>
              <h2>If it is not signed off, it did not happen.</h2>
              <p>
                Every building gets a written checklist built at the walkthrough — your rooms, your
                priorities, your lock-up procedure. The crew works it and closes it out on site.
              </p>
              <p>
                That is what makes a complaint resolvable instead of an argument. You are not asking
                us whether the restroom was done. You are looking at whether it was signed.
              </p>
            </div>

            <ul className="timeline">
              <li>
                <span className="t-time">On site</span>
                <span className="t-what">
                  Clocked in at the building
                  <span className="t-note">Not from a truck in the parking lot.</span>
                </span>
              </li>
              <li>
                <span className="t-time">During</span>
                <span className="t-what">
                  Worked against your checklist
                  <span className="t-note">Written at the walkthrough, not from a template.</span>
                </span>
              </li>
              <li>
                <span className="t-time">End</span>
                <span className="t-what">
                  Closed out and signed by name
                  <span className="t-note">Including lock-up and alarm.</span>
                </span>
              </li>
              <li>
                <span className="t-time">Morning</span>
                <span className="t-what">
                  You have the record
                  <span className="t-note">Without having to ask for it.</span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {SITE.claims.guaranteeCommitted && (
        <section className="section">
          <div className="shell-narrow">
            <div className="guarantee">
              <p className="eyebrow">Our guarantee</p>
              <h2>{GUARANTEE.headline}</h2>
              <p>{GUARANTEE.body}</p>
              <p className="small">
                Most cleaning companies write &ldquo;Satisfaction Guaranteed&rdquo; and never define
                it, which makes it worth nothing. Ours has a deadline and a consequence.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="section section-ground">
        <div className="shell">
          <div className="split">
            <div className="stack">
              <p className="eyebrow">What we will not do</p>
              <h2>The things that make this industry hard to trust.</h2>
              <ul className="dash">
                <li>Lock you into an annual contract with a termination fee.</li>
                <li>Subcontract your building to someone we have not screened.</li>
                <li>Underbid the walkthrough and then short the crew to make the margin.</li>
                <li>Rotate a different team through until nobody knows your building.</li>
                <li>Quote a number that changes after you sign.</li>
                <li>Claim years we have not been in business.</li>
              </ul>
            </div>

            <div className="stack split-sticky">
              <div className="callout">
                <p>
                  <strong>Month to month, 30 days out, either way.</strong> About 96% of janitorial
                  contracts already carry a 30-day clause — the difference is that we say so on the
                  website instead of on page four.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dawn">
        <div className="shell">
          <div className="cta-inner">
            <h2>Judge it on the walkthrough.</h2>
            <p>
              Twenty minutes. You will know from the questions we ask whether we have done this
              before.
            </p>
            <div className="btn-row">
              <Link to="/quote" className="btn btn-primary">
                Book a walkthrough
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const PILLARS = [
  {
    title: 'Employees, not subcontractors',
    body: 'Every technician is on our payroll as a W-2 employee. Subcontracting is how a cleaning company loses control of who is in your building at 9pm — and how it avoids carrying workers’ comp. We do neither.',
  },
  {
    title: 'The same crew, every visit',
    body: 'A set crew is assigned to your building and you will know their names. When someone is out, a trained backup covers the shift. A call-out never becomes a skipped visit.',
  },
  {
    title: 'One named person owns your account',
    body: 'Not a call centre and not a rotating account rep. The most common complaint we found in our research was going through four reps in one contract.',
  },
  {
    title: 'Proof of every visit',
    body: 'Clocked in on site, checklist closed out, lock-up and alarm signed by name. You get the record in the morning rather than having to chase it.',
  },
  {
    title: 'Staffing stated in the quote',
    body: 'If the job takes three people, the quote says three people and three people come. Underbidding and then sending two is how the work quietly degrades in month three.',
  },
  {
    title: 'Scheduled check-ins',
    body: 'We ask you how it is going on a schedule, so you never have to raise it first. A client who goes quiet is not satisfied — they are on their way out.',
  },
];
